import type { Config } from "wagmi"

import type { ApiService } from "@/domains/api/api-service"
import type { ClFarmRepository } from "@/domains/cl-farm/cl-farm-repository"
import type { IEventBus } from "@/domains/events"
import type { ILockLookup } from "@/domains/lock/interfaces"
import type { MinimalPoolRepository } from "@/domains/minimal-pool/minimal-pool-repository"
import type { ITokenLookup } from "@/domains/tokens/types"
import { OrchestratedQueryStore } from "@/domains/library"

import type { GaugeNamespace, IEmissionsConfig } from "./interfaces"
import { ClFarmGaugeConnectorStrategy } from "./connectors/cl-farm-gauge-connector-strategy"
import { UniswapV2GaugeConnectorStrategy } from "./connectors/uniswap-v2-gauge-connector-strategy"
import { GaugeApiTransport } from "./gauge-api-transport"
import { GaugeOnChainTransport, GaugeOnChainTransportAddresses } from "./gauge-on-chain-transport"
import { GaugeOrchestratorFactory } from "./gauge-orchestrator-factory"
import { GaugeRepository } from "./gauge-repository"

export type GaugeNamespaceFactoryParams = {
  config: Config
  chainId: number
  gaugeOnChainTransportAddresses: GaugeOnChainTransportAddresses
  orchestratedQueryStore: OrchestratedQueryStore
  apiService: ApiService
  eventBus: IEventBus
  minimalPoolRepository: MinimalPoolRepository | undefined
  clFarmRepository: ClFarmRepository | undefined
  emissionsConfig: IEmissionsConfig
  tokenLookup: ITokenLookup
  lockLookup: ILockLookup
}

export const GaugeNamespaceFactory = {
  create({
    config,
    chainId,
    gaugeOnChainTransportAddresses,
    orchestratedQueryStore,
    apiService,
    eventBus,
    minimalPoolRepository,
    clFarmRepository,
    emissionsConfig,
    tokenLookup,
    lockLookup,
  }: GaugeNamespaceFactoryParams): GaugeNamespace {
    const gaugeApiTransport = new GaugeApiTransport(apiService)
    const gaugeRepository = new GaugeRepository(eventBus)
    const gaugeOnChainTransport = new GaugeOnChainTransport(config, chainId, gaugeOnChainTransportAddresses)

    // Create connector strategies
    const connectorStrategies = []
    if (minimalPoolRepository) {
      connectorStrategies.push(new UniswapV2GaugeConnectorStrategy(minimalPoolRepository))
    }
    if (clFarmRepository) {
      connectorStrategies.push(new ClFarmGaugeConnectorStrategy(clFarmRepository))
    }

    const gaugeOrchestrator = GaugeOrchestratorFactory.create(
      gaugeRepository,
      gaugeApiTransport,
      gaugeOnChainTransport,
      orchestratedQueryStore,
      eventBus,
      connectorStrategies,
      emissionsConfig,
      tokenLookup,
      lockLookup,
    )

    return {
      Repository: gaugeRepository,
      Orchestrator: gaugeOrchestrator,
    }
  },
}
